import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Wrench,
  Plus,
  Settings,
  RefreshCw,
  Upload,
  Download,
  X,
  Check,
  Clock,
  Building,
  FileText,
  Phone,
  User,
  MessageSquare,
  Calendar,
  Bell,
  ChevronRight,
  Star,
  Shield,
  Zap,
} from "lucide-react";
import { SERVICE_REQUEST_API } from "../../data/api";

// Service Card Component
function ServiceCard({ service, onSelect, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ y: -5, scale: 1.02 }}
      className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden group cursor-pointer"
    >
      <div className="relative h-40 bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/10" />
        <div className="relative z-10 text-center text-white">
          <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
            {service.id === 1 && <Wrench className="w-8 h-8" />}
            {service.id === 2 && <Plus className="w-8 h-8" />}
            {service.id === 3 && <Shield className="w-8 h-8" />}
            {service.id === 4 && <RefreshCw className="w-8 h-8" />}
          </div>
          <h3 className="font-bold text-lg">{service.title}</h3>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-purple-400 transform scale-x-0 group-hover:scale-x-100 transition-transform" />
      </div>
      <div className="p-5">
        <p className="text-gray-600 text-sm mb-4">{service.description}</p>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onSelect(service)}
          className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold shadow-lg shadow-blue-500/30 hover:shadow-xl transition-all flex items-center justify-center gap-2"
        >
          <MessageSquare size={18} />
          Submit Request
        </motion.button>
      </div>
    </motion.div>
  );
}

// Form Modal Component
function ServiceFormModal({ service, onClose, onSubmit, loading }) {
  const [formData, setFormData] = useState({
    customerName: "",
    phone: "",
    issue: "",
    file: null,
    amcType: "",
    industries: [],
  });

  const industries = ["Residential", "Retail", "Medical", "Industrial", "Commercial", "Hospitality"];

  const handleSubmit = (e) => {
    e.preventDefault();
    const industriesString = formData.industries.join(", ");
    onSubmit({ ...formData, industries: industriesString });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 50 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 50 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <X size={20} />
          </button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
              {service.id === 1 && <Wrench className="w-7 h-7" />}
              {service.id === 2 && <Plus className="w-7 h-7" />}
              {service.id === 3 && <Shield className="w-7 h-7" />}
              {service.id === 4 && <RefreshCw className="w-7 h-7" />}
            </div>
            <div>
              <h2 className="text-xl font-bold">{service.title}</h2>
              <p className="text-white/80 text-sm">Request Form</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Customer Name */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <User size={16} />
              Customer Name
            </label>
            <input
              type="text"
              value={formData.customerName}
              onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
              placeholder="Enter your name"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
              required
            />
          </div>

          {/* Phone */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Phone size={16} />
              Phone / Address / Model
            </label>
            <input
              type="text"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="Enter phone number"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
              required
            />
          </div>

          {/* Issue */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <MessageSquare size={16} />
              Describe the Issue
            </label>
            <textarea
              value={formData.issue}
              onChange={(e) => setFormData({ ...formData, issue: e.target.value })}
              placeholder="Describe your issue in detail"
              rows={4}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all resize-none"
              required
            />
          </div>

          {/* AMC Type (For Service 3) */}
          {service.id === 3 && (
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <Shield size={16} />
                AMC Contract Type
              </label>
              <select
                value={formData.amcType}
                onChange={(e) => setFormData({ ...formData, amcType: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
              >
                <option value="">-- Select AMC Contract --</option>
                <option value="Premium">Premium AMC Contract</option>
                <option value="Economy">Economy AMC Contract</option>
              </select>
            </div>
          )}

          {/* Industries (For Service 3) */}
          {service.id === 3 && (
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                <Building size={16} />
                Industries We Serve
              </label>
              <div className="grid grid-cols-2 gap-2">
                {industries.map((industry) => (
                  <label
                    key={industry}
                    className={`flex items-center gap-2 p-3 rounded-xl cursor-pointer transition-all ${
                      formData.industries.includes(industry)
                        ? "bg-blue-50 border border-blue-200"
                        : "bg-gray-50 hover:bg-gray-100 border border-transparent"
                    }`}
                  >
                    <input
                      type="checkbox"
                      value={industry}
                      checked={formData.industries.includes(industry)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({ ...formData, industries: [...formData.industries, industry] });
                        } else {
                          setFormData({ ...formData, industries: formData.industries.filter((i) => i !== industry) });
                        }
                      }}
                      className="w-4 h-4 rounded text-blue-600"
                    />
                    <span className="text-sm text-gray-700">{industry}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* File Upload */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <FileText size={16} />
              Attach File (Optional)
            </label>
            <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center hover:border-blue-400 transition-colors cursor-pointer">
              <input
                type="file"
                onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                {formData.file ? (
                  <div className="flex items-center justify-center gap-2 text-blue-600">
                    <FileText size={24} />
                    <span>{formData.file.name}</span>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">Click to upload file</p>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold shadow-lg shadow-blue-500/30 hover:shadow-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <Check size={20} />
                Submit Request
              </>
            )}
          </motion.button>
        </form>
      </motion.div>
    </motion.div>
  );
}

// Notifications Modal
function NotificationsModal({ show, onClose, requests }) {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 50 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white relative">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
              >
                <X size={20} />
              </button>
              <h2 className="text-xl font-bold flex items-center gap-3">
                <Bell className="w-6 h-6" />
                Your Requests
              </h2>
              <p className="text-white/80 text-sm mt-1">{requests.length} total requests</p>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {requests.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">No Requests Yet</h3>
                  <p className="text-gray-500">Submit a request to see it here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {requests.map((req, index) => (
                    <motion.div
                      key={req.requestId || index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-gray-800">{req.service}</h4>
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                          {req.amcType || "Pending"}
                        </span>
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <p className="flex items-center gap-2">
                          <User size={14} /> {req.customerName}
                        </p>
                        <p className="flex items-center gap-2">
                          <Phone size={14} /> {req.phone}
                        </p>
                        <p className="flex items-center gap-2">
                          <MessageSquare size={14} /> {req.issue}
                        </p>
                        <p className="flex items-center gap-2">
                          <Calendar size={14} /> {new Date(req.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Stats Card
function StatsCard({ icon: Icon, label, value, color, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );
}

const ServicesDirectory = () => {
  const services = [
    { id: 1, title: "Elevator Repair", description: "Professional repair services for all elevator types and brands." },
    { id: 2, title: "Elevator Installation", description: "Expert installation of new elevators for residential and commercial buildings." },
    { id: 3, title: "Maintenance Contract", description: "Comprehensive AMC plans tailored to your elevator needs." },
    { id: 4, title: "Modernization", description: "Upgrade and modernize old elevators with latest technology." },
  ];

  const [selectedService, setSelectedService] = useState(null);
  const [submittedData, setSubmittedData] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchAllRequests = async () => {
    try {
      const res = await axios.get(SERVICE_REQUEST_API.GET_ALL);
      setSubmittedData(res.data);
    } catch (err) {
      console.log(err);
      toast.error("Failed to load requests");
    }
  };

  useEffect(() => {
    fetchAllRequests();
  }, []);

  const handleSubmit = async (formData) => {
    setLoading(true);
    try {
      const payload = {
        service: selectedService.title,
        customerName: formData.customerName,
        phone: formData.phone,
        issue: formData.issue,
        amcType: formData.amcType,
        industries: formData.industries,
        fileName: formData.file?.name || "No File",
        createdAt: new Date().toISOString(),
      };

      await axios.post(SERVICE_REQUEST_API.CREATE, payload);

      toast.success("Request submitted successfully!", {
        icon: "✅",
        style: { borderRadius: "12px" },
      });

      setSelectedService(null);
      fetchAllRequests();
    } catch (error) {
      console.error(error);
      toast.error("Failed to submit request");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (submittedData.length === 0) return;
    const dataStr = JSON.stringify(submittedData, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `service_requests_${Date.now()}.json`;
    link.click();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-wrap items-center justify-between gap-4 mb-8"
        >
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
              <Settings className="text-blue-600" />
              Services Directory
            </h1>
            <p className="text-gray-500 mt-1">Request elevator services and maintenance</p>
          </div>
          <div className="flex items-center gap-3">
            {submittedData.length > 0 && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleDownload}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-medium shadow-lg shadow-green-500/30"
              >
                <Download size={18} />
                Download
              </motion.button>
            )}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowNotifications(true)}
              className="relative flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 rounded-xl font-medium shadow-lg hover:shadow-xl transition-all"
            >
              <Bell className="text-blue-600" />
              Requests
              <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {submittedData.length}
              </span>
            </motion.button>
          </div>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <StatsCard icon={Wrench} label="Repair Services" value="24/7" color="from-blue-500 to-cyan-500" delay={0} />
          <StatsCard icon={Shield} label="Active AMCs" value={submittedData.filter(r => r.amcType).length} color="from-green-500 to-emerald-500" delay={0.1} />
          <StatsCard icon={Check} label="Completed" value={submittedData.length} color="from-purple-500 to-pink-500" delay={0.2} />
          <StatsCard icon={Zap} label="Response Time" value="2hrs" color="from-orange-500 to-red-500" delay={0.3} />
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <ServiceCard key={service.id} service={service} index={index} onSelect={setSelectedService} />
          ))}
        </div>

        {/* Form Modal */}
        <AnimatePresence>
          {selectedService && (
            <ServiceFormModal
              service={selectedService}
              onClose={() => setSelectedService(null)}
              onSubmit={handleSubmit}
              loading={loading}
            />
          )}
        </AnimatePresence>

        {/* Notifications Modal */}
        <NotificationsModal
          show={showNotifications}
          onClose={() => setShowNotifications(false)}
          requests={submittedData}
        />
      </div>
    </div>
  );
};

export default ServicesDirectory;
